import React, { useState } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { SubscriptionStatus } from '../../types';

interface Props {
  subscriptionStatus: SubscriptionStatus | null;
}

const SESSION_KEY = 'sub-cancel-banner-dismissed';

function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

export function SubscriptionStatusBanner({ subscriptionStatus }: Props) {
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === 'true'
  );
  const [isLoading, setIsLoading] = useState(false);

  if (!subscriptionStatus) return null;

  const { status, cancelAtPeriodEnd, currentPeriodEnd } = subscriptionStatus;
  const isPastDue = status === 'past_due';
  const isCancelling = cancelAtPeriodEnd && status !== 'canceled';

  if (!isPastDue && !isCancelling) return null;
  if (isCancelling && dismissed) return null;

  async function openPortal() {
    setIsLoading(true);
    try {
      const fns = getFunctions(getApp());
      const createPortal = httpsCallable(fns, 'createPortalSession');
      const result = await createPortal({});
      const { url } = result.data as { url: string };
      window.location.href = url;
    } catch {
      setIsLoading(false);
    }
  }

  function dismiss() {
    sessionStorage.setItem(SESSION_KEY, 'true');
    setDismissed(true);
  }

  if (isPastDue) {
    return (
      <div className="w-full bg-red-50 dark:bg-red-950/40 border-b border-red-200 dark:border-red-800 px-4 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300 truncate">
            Your payment failed. Update your payment method to keep your{' '}
            <span className="font-semibold capitalize">{subscriptionStatus.tier}</span> features.
          </p>
        </div>
        <button
          onClick={openPortal}
          disabled={isLoading}
          className="shrink-0 text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline underline-offset-2 disabled:opacity-50"
        >
          {isLoading ? 'Opening...' : 'Update Payment Method →'}
        </button>
      </div>
    );
  }

  // Scheduled cancellation
  return (
    <div className="w-full bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 px-4 py-2.5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-2.5 min-w-0">
        <Info className="w-4 h-4 text-amber-500 shrink-0" />
        <p className="text-sm text-amber-800 dark:text-amber-300 truncate">
          Your plan is scheduled to cancel on{' '}
          <span className="font-semibold">
            {currentPeriodEnd ? formatDate(currentPeriodEnd) : 'your billing date'}
          </span>
          . You'll keep full access until then.
        </p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button
          onClick={openPortal}
          disabled={isLoading}
          className="text-xs font-semibold text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-200 underline underline-offset-2 disabled:opacity-50"
        >
          {isLoading ? 'Opening...' : 'Reactivate →'}
        </button>
        <button
          onClick={dismiss}
          className="text-amber-500 hover:text-amber-700 dark:hover:text-amber-300"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
